import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "../styles/topConsultants.css";
import LoadingIcon from "../images/Loading_icon.gif";
import TopConsultantRow from "../components/TopConsultants/TopConsultantRow";
import Swal from "sweetalert2";
import JumpingDots from "../images/JumpingDots.gif";
import { AppContext } from "../contexts/AppContext";

const ROWS_PER_PAGE = 100;

function PaginationControls({
  currentPage,
  totalPages,
  totalItems,
  startItem,
  endItem,
  onPageChange,
}) {
  return (
    <div className="mt-2 flex flex-col gap-3 bg-white sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-slate-600">
        Showing{" "}
        <strong>
          {totalItems === 0 ? 0 : startItem} - {endItem}
        </strong>{" "}
        of <strong>{totalItems}</strong> consultants
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={currentPage === 1}
          onClick={() => onPageChange(1)}
          className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          First
        </button>

        <button
          type="button"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Previous
        </button>

        <span className="px-2 text-sm font-medium text-slate-700">
          Page {currentPage} of {totalPages}
        </span>

        <button
          type="button"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>

        <button
          type="button"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(totalPages)}
          className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Last
        </button>
      </div>
    </div>
  );
}

export default function TopConsultants() {
  useEffect(() => {
    document.title = "Top Consultants";
  }, []);

  const { projects, topConsultant, totalConsultant, error, loading } =
    useContext(AppContext);

  const [searchExpand, setSearchExpand] = useState(false);
  const [locationExpand, setLocationExpand] = useState(false);

  const [backupData, setBackupData] = useState([]);
  const [filterName, setFilterName] = useState("");
  const [filterLocation, setFilterLocation] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [showNotification, setShowNotification] = useState(false);
  const [notificationClass, setNotificationClass] = useState("topCon-toast");

  const nameInputRef = useRef(null);
  const locationInputRef = useRef(null);

  if (error) {
    console.log(error);
    Swal.fire({
      icon: "error",
      title: "Oops...",
      text: "Database connection failed!",
    });
  }

  const sortedAndFilteredConsultants = useMemo(() => {
    const nameQuery = filterName.trim().toLowerCase();
    const locationQuery = filterLocation.trim().toLowerCase();

    return [...(topConsultant || [])]
      .sort((a, b) => {
        const nameA = a?.name || "";
        const nameB = b?.name || "";

        return nameA.localeCompare(nameB, undefined, {
          sensitivity: "base",
        });
      })
      .filter((item) => {
        const name = item?.name?.toLowerCase() || "";
        const location = item?.location?.toLowerCase() || "";

        return name.includes(nameQuery) && location.includes(locationQuery);
      });
  }, [topConsultant, filterName, filterLocation]);

  const totalFilteredConsultants = sortedAndFilteredConsultants.length;

  const totalPages = Math.max(
    1,
    Math.ceil(totalFilteredConsultants / ROWS_PER_PAGE)
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterName, filterLocation]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;

  const paginatedConsultants = useMemo(() => {
    return sortedAndFilteredConsultants.slice(startIndex, endIndex);
  }, [sortedAndFilteredConsultants, startIndex, endIndex]);

  const startItem =
    totalFilteredConsultants === 0 ? 0 : startIndex + 1;

  const endItem = Math.min(endIndex, totalFilteredConsultants);

  useEffect(() => {
    if (totalConsultant?.available && totalConsultant?.twoWeekAvailable) {
      setTimeout(() => {
        setShowNotification(true);
        setTimeout(() => {
          setNotificationClass("topCon-toast topCon-active");
          setTimeout(() => {
            setNotificationClass("topCon-toast");
          }, 6000);
        }, 1000);
      }, 500);
    }
  }, [totalConsultant]);

  return (
    <>
      <div className="container-div" style={{ position: "relative" }}>
        <h1 className="totalHeading">
          {loading && !totalFilteredConsultants ? (
            <img src={JumpingDots} alt="" style={{ width: "36px" }} />
          ) : (
            `${totalFilteredConsultants} consultants are found`
          )}
        </h1>

        {showNotification && (
          <div className="notification-section-div">
            <div
              className={notificationClass ? notificationClass : "topCon-toast"}
            >
              <div className="topCon-toast-content">
                <i className="fas fa-solid fa-check topCon-check"></i>
                <div className="topCon-message">
                  <span className="topCon-text topCon-text-1">
                    {totalConsultant?.twoWeekAvailable} consultant(s) will be
                    available in less than two weeks!
                  </span>
                  <span className="topCon-text topCon-text-2">
                    {totalConsultant?.available} consultants are currently
                    available!
                  </span>
                </div>
              </div>
              <i
                className="fa-solid fa-xmark topCon-close"
                onClick={() => setNotificationClass("topCon-toast")}
              ></i>
            </div>
          </div>
        )}
      </div>

      <div className="topCon-table container-div p-[30px]">

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalFilteredConsultants}
          startItem={startItem}
          endItem={endItem}
          onPageChange={setCurrentPage}
        />

        <ul className="topCon-filters list-unstyled form-inline">
          <li className="topCon-name-filter">
            <span className="no-select">Search Name</span>
            <div className="topCon-search-box">
              <input
                className={
                  searchExpand
                    ? "topCon-search-input topCon-filter-expand"
                    : "topCon-search-input"
                }
                placeholder="Search Name"
                onChange={(e) => setFilterName(e.target.value)}
                value={filterName}
                ref={nameInputRef}
              />
              <i
                className="topCon-search-icon fa-solid fa-magnifying-glass"
                onClick={() => {
                  setSearchExpand(!searchExpand);
                }}
              />
            </div>
          </li>

          <li className="topCon-location-filter">
            <span className="no-select">Location</span>
            <div className="topCon-searchLoc-box">
              <input
                className={
                  locationExpand
                    ? "topCon-searchLoc-input topCon-location-expand"
                    : "topCon-searchLoc-input"
                }
                placeholder="Location"
                onChange={(e) => setFilterLocation(e.target.value)}
                value={filterLocation}
                ref={locationInputRef}
              />
              <i
                className="topCon-searchLoc-icon fa-solid fa-magnifying-glass"
                onClick={() => {
                  setLocationExpand(!locationExpand);
                }}
              />
            </div>
          </li>
        </ul>

        <div className="mt-2">
          {paginatedConsultants?.map((topCon) => {
            return (
              <TopConsultantRow
                key={topCon.id}
                topCon={topCon}
                projects={projects}
                backupData={backupData}
                setBackupData={setBackupData}
              />
            );
          })}

          {!loading && totalFilteredConsultants === 0 && (
            <div className="topCon-table-row">
              <div className="topCon-top-div" style={{ height: "45px" }}>
                <h5 style={{ textAlign: "center", paddingTop: "10px" }}>
                  No consultant found
                </h5>
              </div>
              <div className="topCon-row-bottom"></div>
            </div>
          )}
        </div>

        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalFilteredConsultants}
          startItem={startItem}
          endItem={endItem}
          onPageChange={setCurrentPage}
        />

        {loading && (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <img
              src={LoadingIcon}
              alt="Loading..."
              style={{ height: "50px" }}
            />
            <h4 style={{ marginTop: "20px" }}>please wait</h4>
          </div>
        )}
      </div>
    </>
  );
}